import { useTranslation } from "@redotech/redo-i18n";
import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import { ReturnFlow } from "@redotech/redo-return-core-common/return-flow";
import { FlowType } from "@redotech/redo-return-core-common/return-flow-enums";
import { AiSlideOutChat } from "@redotech/redo-web/ai/chat/components/slide-out-chat-modal";
import { AiChatProvider } from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { memo } from "react";
import * as styles from "./flow-agent-chat.module.css";
import { useFlowAgentChat } from "./use-flow-agent-chat";

export const FlowAgentChat = memo(function FlowAgentChat({
  flow,
  flowType,
  onFlowChange,
}: {
  flow?: ReturnFlow;
  flowType: FlowType;
  onFlowChange: (flow: ReturnFlow) => void;
}) {
  const { t } = useTranslation();
  const {
    showChat,
    setShowChat,
    showChatButton,
    handleSendMessageToAI,
    conversationId,
    loading,
  } = useFlowAgentChat({ flow, flowType, onFlowChange });

  if (!showChatButton) {
    return null;
  }

  const hasExistingFlow = flow?.steps && flow.steps.length > 0;
  const welcomeMessage = hasExistingFlow
    ? t(
        "👋 I can help you understand this flow or create a new one!\n\n**Ask questions like:**\n• What does this flow do?\n• What happens if someone tries to return after 30 days?\n• Who pays for shipping?\n\n**Or request changes:**\n• Create a new 60-day return policy\n• Make a flow that allows final sale returns\n\nWhat would you like to know or do?",
      )
    : t(
        "👋 I can create a custom return flow for you!\n\nJust describe your return policy requirements and I'll build a complete flow.\n\n**For example:**\n• Create a 30-day return policy with exchanges\n• Build a flow that requires photos for damage claims\n• Make a policy with different rules for final sale items\n\nWhat kind of return flow do you need?",
      );

  const buttonText = hasExistingFlow
    ? t("Ask AI or Create Flow")
    : t("Generate with AI");

  const chatTitle = t("AI Flow Assistant");

  return (
    <>
      <div className={styles.aiButtonContainer}>
        <RedoButton
          buttonClassName={styles.orangeButton}
          disabled={loading}
          hierarchy="primary"
          IconLeading={AiStarIcon}
          onClick={() => setShowChat(true)}
          size="sm"
          text={buttonText}
          theme="normal"
        />
      </div>
      <AiChatProvider
        conversationId={conversationId}
        defaultMessages={[
          { id: "welcome", role: "assistant", content: welcomeMessage },
        ]}
        sendMessageToAI={handleSendMessageToAI}
      >
        <AiSlideOutChat
          isBeta
          onClose={() => setShowChat(false)}
          open={showChat}
          title={chatTitle}
        />
      </AiChatProvider>
    </>
  );
});
